import React from 'react';
import BaseStep from './BaseStep';
import { SubmitTable } from './Submit/SubmitTable';
import Loader from '../api/Loader';
import Spinner from '../../../../components/Spinner/Spinner';
import getLoadingText from '../utils/LoadingTexts';
import { removeHandler } from '../components/NavigationPrompt';

/**
 * Submit step class.
 */
export default class SubmitStep extends BaseStep {
  static id = 'submit-step';

  static title = 'Submit';

  // Object methods BELOW -----------------------------------------------------

  constructor(props) {
    super(props);

    this.state = {
      polling: false,
      pollingText: '',
      shouldRedirect: false,
      redirectUrl: '',
    };
  }

  /**
   * Interval ID for polling.
   * @type {number|null}
   */
  intervalId = null;

  #cleanup() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Cleanup on unmounting the component.
   */
  componentWillUnmount() {
    this.#cleanup();
  }

  /**
   * Navigate to a given URL.
   * @param {string} url - The URL to navigate to.
   */
  navigate = url => {
    this.setState({ shouldRedirect: true, redirectUrl: url });
  };

  /**
   * Polls a promise at regular intervals until it resolves.
   * @param {Promise} promise - The promise to poll.
   * @param {Function} resolve - The function to call when the promise resolves.
   * @param {Function} reject - The function to call when the promise rejects.
   * @param {number} interval - The polling interval in milliseconds.
   */
  pollPromise = (promise, resolve, reject, interval = 2000) => {
    this.setState({ polling: true, pollingText: 'Submitting the sequence...' });

    // Use interval only for updating text
    this.intervalId = setInterval(() => {
      this.setState(prevState => ({
        ...prevState,
        pollingText: getLoadingText('submitting'),
      }));
    }, interval);

    // Attach .then() once, outside the interval
    promise
      .then(res => {
        this.#cleanup();
        this.setState({ polling: false });

        resolve(res);
      })
      .catch(err => {
        this.#cleanup();
        this.setState({ polling: false });

        reject(err);
      });
  };

  /**
   * Handles the "Assign sequence to devices" button click.
   * Triggers sequence creation and assignment without immediate deployment.
   */
  handleAssignToDevices = () => {
    const submitPromise = Loader.submitWizard(
      this.wizardData.sequence,
      this.wizardData.selectedDeviceAssignments,
      this.wizardData.targetCampaign.campaignId,
      false,
      this.wizardData.schedule,
    );

    this.pollPromise(
      submitPromise,
      result => {
        this.notification.success('Sequence assigned to devices successfully', { result });
        this.navigate(`/sequences/${result.sequenceId}`);
      },
      this.errorHandler,
    );
  };

  /**
   * Handles the "Assign sequence and deploy" button click.
   * Triggers sequence creation, assignment, and immediate deployment.
   */
  handleAssignAndDeploy = () => {
    const submitPromise = Loader.submitWizard(
      this.wizardData.sequence,
      this.wizardData.selectedDeviceAssignments,
      this.wizardData.targetCampaign.campaignId,
      true,
      this.wizardData.schedule,
    );

    this.pollPromise(
      submitPromise,
      result => {
        this.notification.success('Sequence assigned and deployed successfully', { result });
        this.navigate(`/sequences/${result.sequenceId}`);
      },
      this.errorHandler,
    );
  };

  /**
   * Handles errors during submission.
   * @param {Error} error - The error that occurred.
   */
  errorHandler = error => {
    this.notification.error('An error occurred during submission', error);
  };

  /**
   * Renders the step component.
   * @returns {JSX.Element} The step component.
   */
  render() {
    if (this.state.shouldRedirect) {
      // Clean up navigation prompt handler
      removeHandler();

      return this.renderRedirect(this.state.redirectUrl);
    }

    return (
      <Spinner loading={this.state.polling} extraText={this.state.pollingText}>
        {this.renderNavigationPrompt(!this.state.shouldRedirect)}
        <div className="submit-step">
          <SubmitTable
            assignmentsList={Object.values(this.wizardData.selectedDeviceAssignments)}
            footer
          />
          <div className="button-group">
            <button
              data-test="submit-assign-to-devices"
              type="button"
              className="btn-success"
              onClick={this.handleAssignToDevices}
            >
              Assign to devices
            </button>
            <button
              data-test="submit-assign-and-deploy"
              type="button"
              className="btn-success"
              onClick={this.handleAssignAndDeploy}
            >
              Assign and deploy
            </button>
          </div>
        </div>
      </Spinner>
    );
  }
}
